import {
  useGetEducationStrategistTime,
  useGetEducationStrategistWorkload,
  useGetSystemMeetingMetrics,
} from '@actions';
import { AcademicCapIcon, CurrencyEuroIcon, SparklesIcon } from '@heroicons/react/outline';
import { EDUCATION_STRATEGY_REASONS } from '@shared/constants';
import { Spin } from 'antd';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import {
  ColumnHeader,
  EmptyState,
  formatEur,
  formatSeconds,
  formatSignedSeconds,
  MetricCard,
  SectionHeading,
  TableShell,
} from './common';

type MeetingCounts = {
  total: number;
  completed: number;
  scheduled: number;
  cancelled: number;
};

type MeetingRow = MeetingCounts & { label: string; isSubRow?: boolean };

const EMPTY_COUNTS: MeetingCounts = { total: 0, completed: 0, scheduled: 0, cancelled: 0 };

const MeetingCountCells = (props: { counts: MeetingCounts }) => {
  const { counts } = props;

  return (
    <>
      <td className="px-5 py-3 text-right font-semibold tabular-nums">{counts.total}</td>
      <td className="px-5 py-3 text-right tabular-nums">{counts.completed}</td>
      <td className="px-5 py-3 text-right tabular-nums">{counts.scheduled}</td>
      <td className="px-5 py-3 text-right tabular-nums">{counts.cancelled}</td>
    </>
  );
};

const EducationStrategyTab = (props: { month: string }) => {
  const { month } = props;

  const { data: systemMeetings, isLoading: meetingsLoading } = useGetSystemMeetingMetrics({
    month,
  });
  const { data: strategistTime, isLoading: strategistLoading } = useGetEducationStrategistTime({
    month,
  });
  const { data: strategistWorkload, isLoading: workloadLoading } =
    useGetEducationStrategistWorkload({ month });

  const onboarding = systemMeetings?.onboarding ?? EMPTY_COUNTS;
  const educationStrategy = systemMeetings?.educationStrategy;

  const rows: MeetingRow[] = [
    { label: 'Onboarding', ...onboarding },
    { label: 'Education strategy', ...(educationStrategy ?? EMPTY_COUNTS) },
    ...(educationStrategy?.byReason ?? []).map((reasonRow) => ({
      label:
        reasonRow.reason === 'unknown'
          ? 'Reason not recorded'
          : EDUCATION_STRATEGY_REASONS[reasonRow.reason],
      isSubRow: true,
      total: reasonRow.total,
      completed: reasonRow.completed,
      scheduled: reasonRow.scheduled,
      cancelled: reasonRow.cancelled,
    })),
  ];

  const totals: MeetingCounts = {
    total: onboarding.total + (educationStrategy?.total ?? 0),
    completed: onboarding.completed + (educationStrategy?.completed ?? 0),
    scheduled: onboarding.scheduled + (educationStrategy?.scheduled ?? 0),
    cancelled: onboarding.cancelled + (educationStrategy?.cancelled ?? 0),
  };

  const totalOverSeconds = (strategistTime ?? []).reduce((sum, item) => sum + item.overSeconds, 0);
  const totalUnderSeconds = (strategistTime ?? []).reduce(
    (sum, item) => sum + item.underSeconds,
    0
  );
  const totalNetSeconds = totalOverSeconds - totalUnderSeconds;

  const workloadTotals = (strategistWorkload ?? []).reduce(
    (totals, strategist) => ({
      mentoringHeldMeetings: totals.mentoringHeldMeetings + strategist.mentoringHeldMeetings,
      mentoringScheduledMeetings:
        totals.mentoringScheduledMeetings + strategist.mentoringScheduledMeetings,
      educationStrategyMeetings:
        totals.educationStrategyMeetings + strategist.educationStrategyMeetings,
      onboardingMeetings: totals.onboardingMeetings + strategist.onboardingMeetings,
      totalMeetings: totals.totalMeetings + strategist.totalMeetings,
      salaryInEur: totals.salaryInEur + strategist.salaryInEur,
      salaryInEurWithVat: totals.salaryInEurWithVat + strategist.salaryInEurWithVat,
    }),
    {
      mentoringHeldMeetings: 0,
      mentoringScheduledMeetings: 0,
      educationStrategyMeetings: 0,
      onboardingMeetings: 0,
      totalMeetings: 0,
      salaryInEur: 0,
      salaryInEurWithVat: 0,
    }
  );

  const hasVatOnSalaries = workloadTotals.salaryInEurWithVat > workloadTotals.salaryInEur;

  if (meetingsLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          accent="teal"
          icon={<AcademicCapIcon className="h-5 w-5" />}
          value={onboarding.total}
          label="Onboarding meetings"
        />
        <MetricCard
          accent="lila"
          icon={<SparklesIcon className="h-5 w-5" />}
          value={educationStrategy?.total ?? 0}
          label="Education strategy meetings"
        />
        <MetricCard
          accent="gold"
          icon={<CurrencyEuroIcon className="h-5 w-5" />}
          value={
            <span className="flex flex-col">
              <span>{formatEur(workloadTotals.salaryInEurWithVat)}</span>
              {hasVatOnSalaries ? (
                <span className="text-xs font-normal text-customGrey">
                  {formatEur(workloadTotals.salaryInEur)} without VAT
                </span>
              ) : null}
            </span>
          }
          label={hasVatOnSalaries ? 'Strategist salaries (VAT incl.)' : 'Strategist salaries'}
        />
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Onboarding & education strategy meetings"
          description="System meetings scheduled in the selected month, broken down by the reason they were booked. Rescheduled meetings are counted once, on their latest date."
        />
        {totals.total > 0 ? (
          <TableShell minWidth="min-w-[720px]">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-5 py-3 text-left font-semibold">Meeting type</th>
                <ColumnHeader dot="teal">Meetings</ColumnHeader>
                <ColumnHeader dot="green">Completed</ColumnHeader>
                <ColumnHeader dot="gold">Scheduled</ColumnHeader>
                <ColumnHeader dot="lila">Cancelled / no-show</ColumnHeader>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.label}
                  className="border-b border-white/5 transition-colors last:border-b-0 hover:bg-surfaceHover"
                >
                  <td
                    className={mergeClassNames(
                      'px-5 py-3 text-left',
                      row.isSubRow ? 'pl-10 text-customGrey' : 'font-semibold text-customLight'
                    )}
                  >
                    {row.label}
                  </td>
                  <MeetingCountCells counts={row} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 font-semibold text-white">
                <td className="px-5 py-3 text-left">Total</td>
                <MeetingCountCells counts={totals} />
              </tr>
            </tfoot>
          </TableShell>
        ) : (
          <EmptyState>No onboarding or education strategy meetings this month.</EmptyState>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Education strategists · meetings & salary"
          description={`Meetings each strategist held or has booked in the selected month, split by type. Mentoring is split into meetings already done and ones still scheduled. Cancelled, no-show and rescheduled-away meetings are excluded. Salary is the "Salary" extra service booked for that month, converted to EUR, shown with VAT for mentors registered as VAT payers.`}
        />
        {workloadLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : strategistWorkload && strategistWorkload.length > 0 ? (
          <TableShell minWidth="min-w-[820px]">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-5 py-3 text-left font-semibold">Education strategist</th>
                <ColumnHeader dot="teal">Mentoring done</ColumnHeader>
                <ColumnHeader dot="teal">Mentoring scheduled</ColumnHeader>
                <ColumnHeader dot="lila">Education strategy</ColumnHeader>
                <ColumnHeader dot="green">Onboarding</ColumnHeader>
                <ColumnHeader>Total meetings</ColumnHeader>
                <ColumnHeader dot="gold">Salary (EUR)</ColumnHeader>
              </tr>
            </thead>
            <tbody>
              {strategistWorkload.map((strategist) => (
                <tr
                  key={strategist.strategistId}
                  className="border-b border-white/5 transition-colors last:border-b-0 hover:bg-surfaceHover"
                >
                  <td className="px-5 py-3 text-left text-customLight">
                    {strategist.strategistName}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {strategist.mentoringHeldMeetings}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {strategist.mentoringScheduledMeetings}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {strategist.educationStrategyMeetings}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {strategist.onboardingMeetings}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold tabular-nums">
                    {strategist.totalMeetings}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {strategist.salaryInEur > 0 ? (
                      <span className="flex flex-col items-end">
                        <span>
                          {formatEur(strategist.salaryInEurWithVat)}
                          {strategist.isVatPayer ? ' with VAT' : ''}
                        </span>
                        {strategist.isVatPayer ? (
                          <span className="text-xs text-customGrey">
                            {formatEur(strategist.salaryInEur)} without VAT
                          </span>
                        ) : null}
                        {strategist.salaryCurrency && strategist.salaryCurrency !== 'EUR' ? (
                          <span className="text-xs text-customGrey">
                            {strategist.salaryAmount} {strategist.salaryCurrency}
                            {strategist.isVatPayer ? ' without VAT' : ''}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-customGrey">
                        {strategist.isSalaried ? 'Not recorded' : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 font-semibold text-white">
                <td className="px-5 py-3 text-left">Total</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {workloadTotals.mentoringHeldMeetings}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {workloadTotals.mentoringScheduledMeetings}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {workloadTotals.educationStrategyMeetings}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {workloadTotals.onboardingMeetings}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {workloadTotals.totalMeetings}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  <span className="flex flex-col items-end">
                    <span>{formatEur(workloadTotals.salaryInEurWithVat)}</span>
                    {hasVatOnSalaries ? (
                      <span className="text-xs font-normal text-customGrey">
                        {formatEur(workloadTotals.salaryInEur)} without VAT
                      </span>
                    ) : null}
                  </span>
                </td>
              </tr>
            </tfoot>
          </TableShell>
        ) : (
          <EmptyState>No mentor is flagged as an education strategist yet.</EmptyState>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Education strategists · time vs 50m"
          description="Billable time above and below the 50-minute mark, for sessions held in the selected month. Sessions that are still scheduled or pending are excluded."
        />
        {strategistLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : strategistTime && strategistTime.length > 0 ? (
          <TableShell>
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-5 py-3 text-left font-semibold">Education strategist</th>
                <ColumnHeader>Sessions</ColumnHeader>
                <ColumnHeader dot="gold">Time over 50m</ColumnHeader>
                <ColumnHeader dot="lila">Time under 50m</ColumnHeader>
                <ColumnHeader dot="teal">Total</ColumnHeader>
              </tr>
            </thead>
            <tbody>
              {strategistTime.map((strategist) => {
                const netSeconds = (strategist.overSeconds ?? 0) - (strategist.underSeconds ?? 0);

                return (
                  <tr
                    key={strategist.strategistId}
                    className="border-b border-white/5 transition-colors last:border-b-0 hover:bg-surfaceHover"
                  >
                    <td className="px-5 py-3 text-left text-customLight">
                      {strategist.strategistName}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{strategist.sessions}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {formatSeconds(strategist.overSeconds)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {formatSeconds(strategist.underSeconds)}
                    </td>
                    <td
                      className={mergeClassNames(
                        'px-5 py-3 text-right font-semibold tabular-nums',
                        {
                          'text-customGreen': netSeconds > 0,
                          'text-customRed': netSeconds < 0,
                        }
                      )}
                    >
                      {formatSignedSeconds(netSeconds)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 font-semibold text-white">
                <td className="px-5 py-3 text-left">Total</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {strategistTime.reduce((sum, item) => sum + item.sessions, 0)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatSeconds(totalOverSeconds)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatSeconds(totalUnderSeconds)}
                </td>
                <td
                  className={mergeClassNames('px-5 py-3 text-right tabular-nums', {
                    'text-customGreen': totalNetSeconds > 0,
                    'text-customRed': totalNetSeconds < 0,
                  })}
                >
                  {formatSignedSeconds(totalNetSeconds)}
                </td>
              </tr>
            </tfoot>
          </TableShell>
        ) : (
          <EmptyState>No mentor is flagged as an education strategist yet.</EmptyState>
        )}
      </section>
    </div>
  );
};

export default EducationStrategyTab;
